/**
 * v1.3.1 — Inline movie drill-in view.
 *
 * Replaces the popup MediaInfoModal. Rendered as a normal block
 * inside <Browse> (same pattern as the series drill-in), so:
 *   • No `backdrop-blur-sm` overlay (was ~600 ms of GPU work on
 *     TV boxes and made the app feel frozen).
 *   • No "empty modal" state — we always try a name-based TMDB
 *     lookup so the pane has metadata even when the provider ships
 *     no `xc_id` / `tmdb_id`.
 *
 * Buttons: Play, Add to favorites, Add to movie cue, Watch trailer.
 */
import React, { useEffect, useState } from "react";
import {
  Play,
  ChevronLeft,
  Star,
  Calendar,
  Clock,
  Film,
  Users,
  Popcorn,
  Youtube,
} from "lucide-react";
import PosterImage from "./PosterImage";
import { usePlaylistData } from "../lib/PlaylistDataContext";
import { fetchVodInfo } from "../lib/xtreamApi";
import { fetchTmdbMovie } from "../lib/playerApi";
import { getCachedTmdb } from "../lib/tmdbCache";
import { isFavorited, toggleFavorite } from "../lib/favorites";
import { isInCue, toggleCue as toggleMovieCue } from "../lib/movieCue";

// Trailer modal — same as the one that lived inside MediaInfoModal.
function TrailerModal({ ytKey, title, onClose }) {
  return (
    <div
      data-testid="trailer-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/95 p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl aspect-video bg-black rounded-xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 px-3 py-1.5 rounded-md bg-black/70 hover:bg-black text-white text-xs font-semibold"
        >
          Close
        </button>
        <iframe
          className="absolute inset-0 w-full h-full"
          src={`https://www.youtube.com/embed/${ytKey}?autoplay=1&rel=0`}
          title={title || "Trailer"}
          frameBorder="0"
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
        />
      </div>
    </div>
  );
}

export default function MovieDetail({ item, onBack, onPlay }) {
  const { xtream } = usePlaylistData();
  const [details, setDetails] = useState(null);
  const [tmdb, setTmdb] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [trailerOpen, setTrailerOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [actionTick, setActionTick] = useState(0);

  useEffect(() => {
    if (!item) {
      setDetails(null);
      setTmdb(null);
      return;
    }
    let cancelled = false;
    setDetails(null);
    setTmdb(null);
    setLoadingDetails(true);

    // 1) If the provider gave us a VOD id, fetch its full get_vod_info.
    const canGetVod =
      xtream?.creds && item.xc_kind === "vod" && item.xc_id;
    if (canGetVod) {
      fetchVodInfo(xtream.creds, item.xc_id)
        .then((d) => {
          if (cancelled) return;
          setDetails(d || null);
          // Try TMDB via the provider's id if returned.
          const tmdbId =
            d?.info?.tmdb_id || d?.info?.tmdb || item.tmdb_id;
          if (tmdbId) {
            fetchTmdbMovie(tmdbId).then((t) => {
              if (!cancelled) setTmdb(t || null);
            });
          }
        })
        .catch(() => {
          if (!cancelled) setDetails(null);
        })
        .finally(() => {
          if (!cancelled) setLoadingDetails(false);
        });
    } else {
      setLoadingDetails(false);
    }

    // 2) ALWAYS try TMDB in parallel — either by known tmdb_id, or by
    //    name-search fallback. This is the fix for the "modal with no
    //    info" complaint: even when there's no VOD id + no tmdb_id we
    //    still fill the plot / poster / year from a title lookup.
    if (item.tmdb_id) {
      fetchTmdbMovie(item.tmdb_id).then((t) => {
        if (!cancelled && t) setTmdb(t);
      });
    } else if (item.name) {
      getCachedTmdb("movie", item.name, item.year).then((t) => {
        if (!cancelled && t) setTmdb(t);
      });
    }

    return () => {
      cancelled = true;
    };
  }, [item, xtream]);

  if (!item) return null;

  const info = details?.info || {};
  const movie = details?.movie_data || {};

  const poster =
    info.cover_big ||
    info.movie_image ||
    item.logo_url ||
    item.cover ||
    tmdb?.poster ||
    null;
  const backdrop =
    (Array.isArray(info.backdrop_path) && info.backdrop_path[0]) ||
    tmdb?.backdrop ||
    null;
  const year =
    info.releasedate?.slice(0, 4) ||
    movie.year ||
    item.year ||
    tmdb?.year ||
    null;
  const rating = (() => {
    const r = info.rating || info.rating_kinopoisk || item.rating || tmdb?.rating;
    if (r == null || r === "") return null;
    const n = Number(r);
    return isNaN(n) ? null : n;
  })();
  const plot = info.plot || info.description || item.plot || tmdb?.plot || null;
  const cast = info.cast || info.actors || tmdb?.cast || null;
  const director = info.director || tmdb?.director || null;
  const genre = info.genre || item.group || tmdb?.genre || null;
  const duration = info.duration || tmdb?.duration || null;
  const country = info.country || tmdb?.country || null;
  const trailerYt = tmdb?.trailer_yt_key || null;

  const fav = isFavorited("movie", item.stream_url);
  const cued = isInCue(item.stream_url);

  return (
    <div data-testid="movie-detail" className="max-w-5xl">
      <button
        data-testid="movie-detail-back"
        onClick={() => {
          try {
            window.history.back();
          } catch {
            onBack?.();
          }
        }}
        className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white mb-5 transition-colors"
      >
        <ChevronLeft className="w-4 h-4" />
        Back to Movies
      </button>

      {/* Optional backdrop banner. */}
      {backdrop && (
        <div className="relative mb-6 rounded-2xl overflow-hidden -mt-2">
          <img
            src={backdrop}
            alt=""
            className="w-full h-48 sm:h-64 object-cover"
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d1117] via-[#0d1117]/40 to-transparent" />
        </div>
      )}

      <div className="flex gap-4 sm:gap-6 mb-6 flex-wrap">
        <div className="w-36 sm:w-52 shrink-0 bg-black rounded-xl overflow-hidden border border-white/5">
          <div className="w-full aspect-[2/3]">
            <PosterImage src={poster} alt="" className="w-full h-full object-cover" />
          </div>
        </div>

        <div className="flex-1 min-w-[220px]">
          <h2 className="text-xl sm:text-2xl font-bold mb-1">{item.name}</h2>
          <div className="text-xs text-slate-400 flex flex-wrap gap-x-3 gap-y-1 mb-2">
            {year && (
              <span className="inline-flex items-center gap-1">
                <Calendar className="w-3 h-3" /> {year}
              </span>
            )}
            {duration && (
              <span className="inline-flex items-center gap-1">
                <Clock className="w-3 h-3" /> {duration}
              </span>
            )}
            {rating != null && rating > 0 && (
              <span className="inline-flex items-center gap-1 text-amber-300">
                <Star className="w-3 h-3 fill-current" /> {rating.toFixed(1)}
              </span>
            )}
            {genre && (
              <span className="text-slate-500 truncate max-w-[240px]">{genre}</span>
            )}
            {country && <span className="text-slate-600">{country}</span>}
          </div>

          {loadingDetails && (
            <div className="text-[11px] text-slate-500 mt-2">
              Fetching info from provider…
            </div>
          )}

          {plot && (
            <p className="text-sm text-slate-300 mt-3 leading-relaxed max-h-40 overflow-y-auto pr-1">
              {plot}
            </p>
          )}

          <div className="mt-3 space-y-1 text-[12px] text-slate-400">
            {director && (
              <div className="flex items-start gap-2">
                <Film className="w-3 h-3 mt-1 shrink-0" />
                <span>
                  <span className="text-slate-500">Director:</span>{" "}
                  <span className="text-slate-300">{director}</span>
                </span>
              </div>
            )}
            {cast && (
              <div className="flex items-start gap-2">
                <Users className="w-3 h-3 mt-1 shrink-0" />
                <span className="line-clamp-2">
                  <span className="text-slate-500">Cast:</span>{" "}
                  <span className="text-slate-300">{cast}</span>
                </span>
              </div>
            )}
          </div>

          {/* Action buttons: Play + Favorite + Cue + Trailer. */}
          <div className="mt-5 flex flex-wrap gap-2 items-center">
            <button
              data-testid="movie-detail-play"
              onClick={() => onPlay?.(item)}
              className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-5 py-2 rounded-md flex items-center gap-2 active:scale-95 transition-transform"
            >
              <Play className="w-4 h-4 fill-current" />
              Play
            </button>

            <button
              data-testid="movie-detail-fav"
              onClick={() => {
                toggleFavorite("movie", { ...item, content_type: "movie" });
                setActionTick((t) => t + 1);
              }}
              className={
                "inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium border transition-colors " +
                (fav
                  ? "bg-amber-400/95 text-black border-amber-400"
                  : "bg-white/5 border-white/10 text-slate-100 hover:bg-white/10")
              }
            >
              <Star className={`w-4 h-4 ${fav ? "fill-current" : ""}`} />
              {fav ? "In favorites" : "Add to favorites"}
            </button>

            <button
              data-testid="movie-detail-cue"
              onClick={() => {
                toggleMovieCue({ ...item, content_type: "movie" });
                setActionTick((t) => t + 1);
              }}
              className={
                "inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium border transition-colors " +
                (cued
                  ? "bg-emerald-500/90 text-white border-emerald-400"
                  : "bg-white/5 border-white/10 text-slate-100 hover:bg-white/10")
              }
            >
              <Popcorn className="w-4 h-4" />
              {cued ? "In movie cue" : "Add to movie cue"}
            </button>

            {trailerYt && (
              <button
                data-testid="movie-detail-trailer"
                onClick={() => setTrailerOpen(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium bg-white/5 border border-white/10 text-slate-100 hover:bg-white/10 transition-colors"
              >
                <Youtube className="w-4 h-4" />
                Watch trailer
              </button>
            )}
          </div>
        </div>
      </div>

      {trailerOpen && trailerYt && (
        <TrailerModal
          ytKey={trailerYt}
          title={item?.name || "Trailer"}
          onClose={() => setTrailerOpen(false)}
        />
      )}
    </div>
  );
}
